import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { IconCheck } from '@tabler/icons';
import clsx from 'clsx';

import TripDTO from '#root/models/TripDTO';
import { tripService } from '#root/services';
import { parseYmd } from '#root/utils';
import { Badge, Button, Card } from '#root/components/ui';
import {
  BriefcaseOutlineIcon,
  HandHoldingIcon,
  InboxIcon,
  MapSolidIcon,
  PlaneSolidIcon,
  PinSolidIcon,
} from '#root/icons';
import styles from './tripCard.module.scss';

/** Fechas de la API ("Y-m-d") a formato legible: "01 sep 2026". */
const formatTripDate = (value?: string | number | Date | null) => {
  const date = parseYmd(value);
  return date ? format(date, 'dd MMM yyyy', { locale: es }) : '—';
};

type MetricVariant = 'metricInfo' | 'metricWarning' | 'metricSuccess';

interface MetricProps {
  icon: React.ReactNode;
  label: string;
  value?: number;
  variant: MetricVariant;
}

const Metric = (props: MetricProps) => {
  const { icon, label, value, variant } = props;
  // Sin actividad: el chip se atenua para que el ojo priorice los que si tienen datos.
  const isEmpty = !value;

  return (
    <div className={clsx(styles.metric, styles[variant], isEmpty && styles.metricEmpty)}>
      <div className={styles.metricIcon}>{icon}</div>
      <div className={styles.metricBody}>
        <p className={styles.metricLabel}>{label}</p>
        <h6 className={styles.metricValue}>{value ?? 0}</h6>
      </div>
    </div>
  );
};

interface TravelItemProps {
  icon: React.ReactNode;
  label?: React.ReactNode;
  value?: string | number | null;
}

const TravelItem = (props: TravelItemProps) => {
  const { icon, label = '', value = '' } = props;
  return (
    <div className={styles.travelItem}>
      <div className={styles.travelIcon}>{icon}</div>
      <div className={styles.travelInfo}>
        {value && <h6 className="guv-ellipsis">{value}</h6>}
        {label && <p>{label}</p>}
      </div>
    </div>
  );
};

interface DetailItemProps {
  label: string;
  value?: string | number | null;
}

const DetailItem = ({ label, value }: DetailItemProps) => (
  <div className={styles.detailItem}>
    <dt className={styles.detailLabel}>{label}</dt>
    <dd className={styles.detailValue}>{value || '—'}</dd>
  </div>
);

interface TripCardProps {
  trip: TripDTO;
  createMessage: any;
  fetchTrips: () => void;
  openDialog: () => void;
  xref?: (node: HTMLDivElement | null) => void; // Para IntersectionObserver (paginación)
}

const TripCard = (props: TripCardProps) => {
  const { trip, createMessage, fetchTrips, openDialog, xref } = props;
  const destinyText =
    trip.destination_country && `Entrega en ${trip.destination_country} - ${trip.lugar_entrega}`;

  const isCreated = trip.estado === 'CREADO';
  const isFinished = trip.estado === 'FINALIZADO';
  const isSelected = trip.is_seleccionado === 1;
  // `=== false` a proposito: con un backend sin `is_offerable` el campo llega
  // `undefined` y la tarjeta debe comportarse como antes (sin ocultar acciones).
  const isExpired = isCreated && trip.is_offerable === false;

  const fetchSelectTrip = async () => {
    const res = await tripService.select(trip.id);
    if (res.error) {
      createMessage.error(res.message);
      return;
    }
    createMessage.success('Viaje seleccionado');
    fetchTrips();
  };

  return (
    <Card ref={xref} noHover className={clsx(styles.root, isFinished && styles.finished)}>
      <div className={styles.header}>
        <h4 className={styles.title}>{trip.nombre}</h4>
        <div className={styles.badges}>
          {isFinished && (
            <Badge variant="default" size="sm">
              Finalizado
            </Badge>
          )}
          {isExpired && (
            <Badge variant="warning" size="sm">
              Vencido
            </Badge>
          )}
          {isCreated && isSelected && (
            <Badge variant="primarySolid" size="md" icon={<IconCheck size={14} />}>
              Seleccionado
            </Badge>
          )}
        </div>
        <div className={styles.actions}>
          {isCreated && (
            <Button variant="outline" onClick={openDialog}>
              Editar
            </Button>
          )}
          {/* Un viaje vencido no se puede seleccionar: el server lo rechaza. */}
          {isCreated && !isSelected && !isExpired && (
            <Button variant="outline" onClick={fetchSelectTrip}>
              Seleccionar
            </Button>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.timeline}>
          <TravelItem
            icon={<PinSolidIcon />}
            value={trip.city}
            label={trip.departure_country}
          />
          <TravelItem
            icon={<PlaneSolidIcon style={{ transform: 'rotate(90deg)' }} />}
            value={formatTripDate(trip.arrive_date)}
            label="Fecha de viaje"
          />
          <TravelItem
            icon={<MapSolidIcon />}
            value={`${formatTripDate(trip.fecha_entrega)}`}
            label={destinyText}
          />
        </div>
        <div className={styles.divider} />
        <dl className={styles.detailGrid}>
          <DetailItem label="Alojamiento" value={trip.accommodation_type} />
          <DetailItem label="Código postal" value={trip.zipcode} />
          <DetailItem label="Adultos" value={trip.adults_qty} />
        </dl>
      </div>
      <div className={styles.metricsRow}>
        <Metric
          icon={<InboxIcon />}
          label="Ofertas"
          value={trip.offers_count}
          variant="metricInfo"
        />
        <Metric
          icon={<BriefcaseOutlineIcon />}
          label="Acuerdos"
          value={trip.deals_count}
          variant="metricWarning"
        />
        <Metric
          icon={<HandHoldingIcon />}
          label="Pagados"
          value={trip.deals_paid_count}
          variant="metricSuccess"
        />
      </div>
    </Card>
  );
};

export default TripCard;
